import {FULFILLED} from 'redux-promise-middleware';
import {pushDataLayer} from '@optimaxdev/analytics/mobile';

import {APP, CATEGORY} from 'constants/actions';
import {FILTERED_CATEGORY} from 'constants/analytics/events';
import {CUSTOM_DIMENSION} from 'constants/analytics/storages';

import {sendCustomDimensionEvent} from 'libs/analytics/setDimension';
import {sessionStore} from 'libs/storage/storage';
import {call, select, takeLatest, take} from 'redux-saga/effects';
import {getTypePage} from 'sagas/analytics/helpers/getTypePage';
import {reduceImpressions} from 'sagas/EEC/reduce';
import {analytics, prepareFilterList} from 'libs/analytics/analytics';
import {getRouter} from 'selectors/router/router';
import {getFilters, getActiveFilters} from 'selectors/filters/filters';

export const PAGE_ROUTE_TYPES = {
    home: {pageType: '', reactPV: ''},
    checkout: {pageType: 'CHECKOUT', reactPV: 'CHECKOUT'},
    category: {pageType: 'CATEGORY', reactPV: 'CATEGORY'},
    product: {pageType: 'PRODUCT', reactPV: 'PRODUCT'},
    productEdit: {pageType: 'PRODUCT', reactPV: 'PRODUCT'},
    cart: {pageType: '', reactPV: ''},
    postPurchaseInsurance: {pageType: '', reactPV: ''},
    blog: {pageType: 'BLOG', reactPV: 'BLOG'},
    profile: {pageType: 'Account', reactPV: 'MY_ACCOUNT'},
    successPage: {pageType: 'TYP', reactPV: 'ORDER SUCCESS'},
    insuranceSuccessPage: {pageType: 'TYP', reactPV: 'ORDER SUCCESS'},
    coupon: {pageType: 'COUPONS', reactPV: 'COUPONS'},
    resetPassword: {pageType: 'OTHER', reactPV: 'CHANGE_FORGOTTEN'},
    orderSearch: {pageType: 'OrderTracking', reactPV: 'DEFAULT'},
    wishlist: {pageType: 'Wishlist', reactPV: 'DEFAULT'},
    contacts: {pageType: 'ContactUs', reactPV: 'DEFAULT'},
    sixOverSix: {pageType: 'Scanner', reactPV: 'DEFAULT'},
    insuranceLP: {pageType: 'VisionInsurance', reactPV: 'DEFAULT'},
    rma: {pageType: 'RMA', reactPV: 'DEFAULT'},
};

/**
 * Pushing a page type to the dataLayer.
 * @param {string} pageType - Page type.
 */
export function* pushPageType(pageType: string) {
    yield call(analytics, {page_type_param2: pageType}, true);
}

/**
 * Parse url to be fetch.
 * @returns {string} Page type.
 */
export function* parsePageTypeFromUrl() {
    const {location} = yield select(getRouter);
    if (!location) return {pageType: '', reactPV: ''};
    const pathName = location.pathname;
    switch (pathName) {
        case '/coupons-and-promotions': {
            return {pageType: 'COUPONS', reactPV: 'COUPONS'};
        }
        case '/salesperson/result/': {
            return {pageType: 'SEARCH RESULTS', reactPV: 'SEARCH RESULTS'};
        }
        default: {
            return {pageType: 'OTHER', reactPV: 'OTHER'};
        }
    }
}

/**
 * Try to fetch current page type.
 * @returns {string} Page type.
 */
export function* fetchPageType() {
    const pageType = yield call(getTypePage);
    if (pageType in PAGE_ROUTE_TYPES) return PAGE_ROUTE_TYPES[pageType];
    return yield call(parsePageTypeFromUrl);
}

/**
 * Send custom dimention event on location change.
 */
export function* sendCustomDimension() {
    const isLocationChanged = yield call([sessionStore, 'get'], CUSTOM_DIMENSION, false);
    if (isLocationChanged) {
        yield call(sendCustomDimensionEvent);
    } else {
        yield call([sessionStore, 'set'], CUSTOM_DIMENSION, true);
    }
}

/**
 * Event handler.
 */
export function* onGetRouterInfo() {
    yield call(sendCustomDimension);
    const {pageType, reactPV} = yield call(fetchPageType);
    if (pageType) {
        yield call(pushPageType, pageType);
    }
    if (reactPV) {
        yield call(reduceImpressions, reactPV);
    }
}

/**
 * Add filter category to DataLayer
 */
export function* pushFilteredCategoryToDataLayer() {
    yield take(`${CATEGORY.GET_CATEGORY_ITEMS_COUNT}_${FULFILLED}`);
    const activeFilters = yield select(getActiveFilters);
    const availableFilters = yield select(getFilters);
    const filters = yield call(prepareFilterList, availableFilters.items, activeFilters);
    yield call(pushDataLayer, {event: FILTERED_CATEGORY, filters});
}

/**
 * Begin of saga.
 */
export default function* dataLayerSaga() {
    yield takeLatest(`${CATEGORY.SET_ACTIVE_FILTERS}`, pushFilteredCategoryToDataLayer);
    yield takeLatest(APP.SAVE_LOCATION, onGetRouterInfo);
}
